import { useState, useEffect, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useLang } from '@/lib/LanguageContext';
import SEO from '../components/SEO';
import GridBackground from '../components/GridBackground';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppFloatButton from '../components/WhatsAppFloatButton';
import BlogHero from '../components/blog/BlogHero';
import BlogFilters from '../components/blog/BlogFilters';
import BlogCard from '../components/blog/BlogCard';

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState('');
  const { language } = useLang();
  const langPosts = useMemo(() => posts.filter((p) => (p.lang || 'en') === (language === 'es' ? 'es' : 'en')), [posts, language]);

  useEffect(() => {
    base44.entities.BlogPost.list('-published_date').then((data) => {
      setPosts(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const featured = useMemo(() => langPosts.find((p) => p.featured) || langPosts[0], [langPosts]);

  const filtered = useMemo(() => {
    return langPosts.filter((p) => {
      if (featured && p.id === featured.id && category === 'All' && !search) return false;
      const matchesCategory = category === 'All' || p.category === category;
      const matchesSearch =
        !search ||
        p.title?.toLowerCase().includes(search.toLowerCase()) ||
        p.excerpt?.toLowerCase().includes(search.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [langPosts, category, search, featured]);

  return (
    <div className="min-h-screen bg-background text-foreground relative">
      <SEO
        fullTitle="Blog | AI, Software & Cybersecurity Insights | Jotunheims"
        description="Practical guides on AI agents, custom software, cybersecurity, mobile apps and SaaS from the Jotunheims team in Dubai."
        path="/blog"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Blog',
          name: 'Jotunheims Blog',
          url: 'https://jotunheims.com/blog',
          publisher: { '@id': 'https://jotunheims.com/#organization' }
        }}
      />
      <GridBackground />
      <Navbar />
      <BlogHero />

      <section className="px-6 pb-24">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center text-muted-foreground py-20">Loading articles...</div>
          ) : (
            <>
              {featured && category === 'All' && !search && (
                <div className="mb-12">
                  <BlogCard post={featured} featured />
                </div>
              )}

              <BlogFilters category={category} setCategory={setCategory} search={search} setSearch={setSearch} />

              {filtered.length === 0 ? (
                <div className="text-center text-muted-foreground py-16 font-inter">
                  No articles match your search.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filtered.map((post) => (
                    <BlogCard key={post.id} post={post} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
      <WhatsAppFloatButton />
    </div>
  );
}